package com.rainbowclubs.rainbow.presentation.login

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.vectorResource
import org.koin.compose.viewmodel.koinViewModel
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.display_name_field_label
import rainbowclubs.composeapp.generated.resources.email_field_label
import rainbowclubs.composeapp.generated.resources.general_error
import rainbowclubs.composeapp.generated.resources.hide_password
import rainbowclubs.composeapp.generated.resources.ic_visible_off
import rainbowclubs.composeapp.generated.resources.logo_image_cd
import rainbowclubs.composeapp.generated.resources.password_field_label
import rainbowclubs.composeapp.generated.resources.rainbow_clubs
import rainbowclubs.composeapp.generated.resources.re_password_field_label
import rainbowclubs.composeapp.generated.resources.signup_button
import rainbowclubs.composeapp.generated.resources.try_again_button
import rainbowclubs.composeapp.generated.resources.ic_visible_on
import rainbowclubs.composeapp.generated.resources.show_password

@Composable
fun SignupScreen(
    viewModel: SignupViewModel = koinViewModel(),
    navigateTo: (SignupViewModel.SignupNavigationEvent) -> Unit
) {

    val state = viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }


    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp)
            .verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Image(
            painter = painterResource(Res.drawable.rainbow_clubs),
            contentDescription = stringResource(Res.string.logo_image_cd),
        )
        Spacer(modifier = Modifier.height(16.dp))

        when (state.value) {
            ScreenState.Loading -> {
                CircularProgressIndicator()
            }

            is ScreenState.Content -> {
                val uiModel = (state.value as ScreenState.Content<SignupViewModel.UIModel>).data

                OutlinedTextField(
                    value = uiModel.username,
                    onValueChange = { viewModel.onUsernameChanged(it) },
                    label = { Text(stringResource(Res.string.email_field_label)) },
                    modifier = Modifier.fillMaxWidth(),
                    isError = uiModel.usernameError != null,
                    supportingText = {
                        uiModel.usernameError?.let { error ->
                            Text(
                                text = stringResource(error),
                                color = MaterialTheme.colorScheme.error,
                            )
                        }
                    }
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = uiModel.displayName,
                    onValueChange = { viewModel.onDisplayNameChanged(it) },
                    label = { Text(stringResource(Res.string.display_name_field_label)) },
                    modifier = Modifier.fillMaxWidth(),
                    isError = uiModel.displayNameError != null,
                    supportingText = {
                        uiModel.displayNameError?.let { error ->
                            Text(
                                text = stringResource(error),
                                color = MaterialTheme.colorScheme.error,
                            )
                        }
                    }
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = uiModel.password,
                    onValueChange = { viewModel.onPasswordChanged(it) },
                    label = { Text(stringResource(Res.string.password_field_label)) },
                    visualTransformation = if (uiModel.passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    modifier = Modifier.fillMaxWidth(),
                    isError = uiModel.passwordError != null,
                    trailingIcon = {
                        val vectorImage = if (uiModel.passwordVisible)
                            Res.drawable.ic_visible_on
                        else
                            Res.drawable.ic_visible_off

                        val description = if (uiModel.passwordVisible)
                            stringResource(Res.string.hide_password)
                        else stringResource(Res.string.show_password)

                        IconButton(
                            onClick = {
                                viewModel.onShowPasswordClicked()
                            }
                        ) {
                            Icon(imageVector = vectorResource(vectorImage), description)
                        }
                    },
                    supportingText = {
                        uiModel.passwordError?.let { error ->
                            Text(
                                text = stringResource(error),
                                color = MaterialTheme.colorScheme.error,
                            )
                        }
                    }
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = uiModel.rePassword,
                    onValueChange = { viewModel.onRePasswordChanged(it) },
                    label = { Text(stringResource(Res.string.re_password_field_label)) },
                    visualTransformation = if (uiModel.rePasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    modifier = Modifier.fillMaxWidth(),
                    isError = uiModel.rePasswordError != null,
                    trailingIcon = {
                        val vectorImage = if (uiModel.rePasswordVisible)
                            Res.drawable.ic_visible_on
                        else
                            Res.drawable.ic_visible_off

                        val description = if (uiModel.rePasswordVisible)
                            stringResource(Res.string.hide_password)
                        else stringResource(Res.string.show_password)

                        IconButton(
                            onClick = {
                                viewModel.onShowRepasswordClicked()
                            }
                        ) {
                            Icon(imageVector = vectorResource(vectorImage), description)
                        }
                    },
                    supportingText = {
                        uiModel.rePasswordError?.let { error ->
                            Text(
                                text = stringResource(error),
                                color = MaterialTheme.colorScheme.error,
                            )
                        }
                    }
                )
                Spacer(modifier = Modifier.height(8.dp))
                Button(
                    onClick = { viewModel.onCreateAccountClicked() },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(stringResource(Res.string.signup_button))
                }
            }

            is ScreenState.Error -> {
                val error = (state.value as ScreenState.Error).e
                ErrorScreen(
                    error = stringResource(Res.string.general_error).plus("\n${error}"),
                    buttonText = stringResource(Res.string.try_again_button),
                    onButtonClick = {
                        viewModel.tryAgain()
                    }
                )
            }
        }

    }
}

@Preview
@Composable
private fun SignupScreenPreview() {
    SignupScreen(
        navigateTo = {}
    )
}
